import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => { setPlans(r.data.plans); setEnabled(r.data.payments_enabled); }).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (plan.id === "free") { nav(user ? "/app" : "/register"); return; }
    const lookup = yearly ? plan.yearly_lookup : plan.monthly_lookup;
    if (!user) {
      sessionStorage.setItem("kreonis_pending_checkout", JSON.stringify({ lookup, name: plan.name }));
      toast.info(`Sign in to continue your ${plan.name} upgrade`);
      nav("/login");
      return;
    }
    if (!enabled) { toast.error("Payments are currently unavailable."); return; }
    setLoading(plan.id);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading("");
    }
  };

  return (
    <MarketingLayout>
      <Seo title="Pricing" description="Free, Alpha Pro ($49/mo) and Quant Advanced ($149/mo) plans. 7-day Alpha Pro trial." />
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-accent">Pricing</span>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-5xl">Simple, transparent plans</h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Start free. Upgrade for more AI signals, backtesting, advanced alerts and AI tools.</p>
          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
            <button data-testid="billing-monthly" onClick={() => setYearly(false)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${!yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Monthly</button>
            <button data-testid="billing-yearly" onClick={() => setYearly(true)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Yearly <span className="text-bullish">Save up to 33%</span></button>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {plans.map((p) => {
            const price = p.id === "free" ? 0 : (yearly ? p.price_yearly : p.price_monthly);
            const isPro = p.id === "pro";
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`relative rounded-3xl border bg-card p-7 ${isPro ? "border-primary shadow-xl shadow-primary/10" : "border-border"}`}>
                {isPro && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground flex items-center gap-1"><Sparkles className="h-3 w-3" /> Most Popular</div>}
                <h3 className="font-display text-xl font-bold">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold">${price}</span>
                  {p.id !== "free" && <span className="mb-1 text-sm text-muted-foreground">/{yearly ? "yr" : "mo"}</span>}
                </div>
                {isPro && <p className="mt-1 text-xs text-bullish">Includes a 7-day free trial</p>}
                <button data-testid={`plan-${p.id}-cta`} disabled={loading === p.id} onClick={() => subscribe(p)}
                  className={`mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${isPro ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" : "border border-border hover:bg-secondary"} disabled:opacity-60`}>
                  {loading === p.id ? "Redirecting…" : p.id === "free" ? "Get Started" : "Choose " + p.name}
                </button>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-bullish" /> <span className="text-muted-foreground">{f}</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {!enabled && <p className="mt-6 text-center text-sm text-warning">Payments are currently unavailable.</p>}
        <p className="mt-6 text-center text-xs text-muted-foreground">Test mode: use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </MarketingLayout>
  );
}
